import React, { useEffect, useState, useRef } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import { Flex, Button, Spin, Breadcrumb } from 'antd'
import classNames from 'classnames'
import {
  DetailDiagram,
  ComponentDetailDiagram,
  useSubsystemByCode,
} from '../BlockDiagram'
import { useUserPlant } from '../hooks/User'
import { useDiagram, useDiagramDispatch } from './DiagramContext'
import {
  ComponentImage,
  ComponentOrLocationImage,
  LocationImage,
} from './Image'
import PartNumberLabel from '../components/PartNumberLabel'
import ConnectorPinout from './ConnectorPinout/DetailDiagramPinout'
import CircuitColorIcon from './CircuitColorIcon'
import ComponentNeighbors from './ComponentNeighbors'
import { RelatedSubsystemsButton } from '../components/RelatedSubsystemsButton'
import { FormattedMessage, useIntl } from 'react-intl'
import { useTerminologyVIN } from '../hooks/Terminology'
import { useMakeModelByVin } from '../hooks/MakeModels'
import {
  RelatedDTCsModal,
  RelatedDTCsButton,
  CircuitDTCsProvider,
} from '../CircuitDTCs'
import TracerErrorBoundary from '../components/TracerErrorBoundary'
import {
  LayoutOutlined,
  HomeOutlined,
  PlusOutlined,
  MinusOutlined,
  LoadingOutlined,
  LeftOutlined,
} from '@ant-design/icons'
import { dispatchEvent } from '../CloudWatch-init'
import {
  NoSelection,
  Breadcrumbs,
  ComponentDetailDiagramBreadcrumbs,
} from './index'

import './style.scss'
import { isWindowBelowMinHeight } from '../helpers/utils'

const DetailInfoPinPanel = ({ toggleTopPanelVisibility }) => {
  const diagram = useDiagram()
  const dispatch = useDiagramDispatch()
  const [pinMaximized, togglePinView] = useState(false)
  const [isImagePanelExanded, setImagePanelExanded] = React.useState(true)

  return (
    <Flex vertical={false} style={{ height: '100%' }}>
      {pinMaximized || (
        <div
          className={classNames('functional-diagrams-detail-pin-info-table', {
            expand: !isImagePanelExanded,
          })}
        >
          <ConnectorPinout pageSize={isWindowBelowMinHeight() ? 4 : 8} />
        </div>
      )}
      <Flex
        className={classNames(
          'functional-diagrams-detail-pin-info-image-panel',
          { minimize: !isImagePanelExanded, 'full-screen': pinMaximized },
        )}
      >
        {pinMaximized || (
          <div className="functional-diagrams-detail-pin-info-image-divider">
            <Button
              size="small"
              shape="circle"
              icon={isImagePanelExanded ? <MinusOutlined /> : <PlusOutlined />}
              onClick={() => setImagePanelExanded(!isImagePanelExanded)}
            />
          </div>
        )}
        <div className="functional-diagrams-detail-pin-info-image">
          <ComponentOrLocationImage
            componentId={diagram.detailDiagram.data.componentId}
            componentImageName={diagram.detailDiagram.data.imageName}
            locationImageName={diagram.detailDiagram.data.locationImageName}
            pins={[diagram.detailDiagram.data.pin]}
            description={diagram.detailDiagram.data.componentDescription}
            alias={diagram.detailDiagram.data.componentAlias}
            isImagePanelExanded={isImagePanelExanded}
            isFullScreen={pinMaximized}
            partNumber={diagram.detailDiagram.data.partNumber}
          >
            {diagram.detailDiagram.data.category !== 'inline' && (
              <RelatedSubsystemsButton
                showButtonText={false}
                vin={diagram.vin}
                componentId={diagram.detailDiagram.data.componentId}
                contextComponentId={
                  diagram.detailDiagram.relatedSubsystemComponentID
                }
                componentIdDispatch={dispatch.handleRelatedSubsystemsButton}
              />
            )}
            <FullScreenButton
              isDisabled={false}
              onClickHandler={() => {
                const panelVisibility = pinMaximized ? true : false
                togglePinView(!pinMaximized)
                toggleTopPanelVisibility(panelVisibility)
              }}
              classNames={
                'functional-diagram-raised-button detail-diagram-detail-collapse-button'
              }
            />
          </ComponentOrLocationImage>
        </div>
      </Flex>
    </Flex>
  )
}

const DetailInfoCircuit = ({ toggleTopPanelVisibility }) => {
  const diagram = useDiagram()
  const dispatch = useDiagramDispatch()
  const {
    circuit,
    circuit_description,
    colors,
    harness,
    harness_description,
    fromComponentImageName,
    toComponentImageName,
    fromPin,
    toPin,
    fromComponentCategory,
    toComponentCategory,
    fromComponent,
    toComponent,
    toLocationImageName,
    fromLocationImageName,
    fromComponentDescription,
    toComponentDescription,
    toComponentAlias,
    fromComponentAlias,
    fromComponentPartNumber,
    toComponentPartNumber,
    isVirtual,
  } = diagram.detailDiagram.data
  const [firstColor, secondColor] = colors.hex_triplets
  const [toComponentMaximized, toggleToComponentView] = useState(false)
  const [fromComponentMaximized, toggleFromComponentView] = useState(false)
  const [showSpliceLocationImage, updateShowSpliceLocationImage] =
    useState(false)
  const timeOnCircuit = useRef(null)
  const [userPlant] = useUserPlant()

  const shouldHideInlineDetails =
    fromComponentCategory !== 'inline' ||
    toComponentCategory !== 'inline' ||
    (!isVirtual &&
      fromComponentCategory == 'inline' &&
      toComponentCategory == 'inline')
      ? true
      : false

  const { data, isLoading } = useTerminologyVIN({
    vin: diagram.vin,
    codes: harness,
  })

  const { isLoading: isMakeModelByVinLoading, makeModel } = useMakeModelByVin({
    vin: diagram.vin,
  })

  useEffect(() => {
    window.addEventListener('beforeunload', dispatchCircuitEvent)
    return () => {
      window.removeEventListener('beforeunload', dispatchCircuitEvent)
    }
  }, [])

  useEffect(() => {
    timeOnCircuit.current = Date.now()
    return () => {
      dispatchCircuitEvent()
    }
  }, [circuit])

  const dispatchCircuitEvent = () => {
    if (timeOnCircuit.current) {
      console.log('sending the following usage event:', {
        circuitId: diagram.detailDiagram.data.circuit,
        timeOnCircuit: Date.now() - timeOnCircuit.current,
        plant: userPlant,
      })
      dispatchEvent('FUNCTIONAL_DIAGRAM_CIRCUIT_UPDATED', {
        circuitId: diagram.detailDiagram.data.circuit,
        timeOnCircuit: Date.now() - timeOnCircuit.current,
        plant: userPlant,
      })
    }
  }

  const harnessDescription =
    data && data[harness] && data[harness].description
      ? data[harness].description
      : harness_description

  const spliceLocationImageDisplayHandler = () => {
    updateShowSpliceLocationImage(!showSpliceLocationImage)
  }

  const renderExpandButton = (buttonState, handlerFunc) => {
    return (
      <FullScreenButton
        isDisabled={false}
        onClickHandler={() => {
          const panelVisibility = buttonState ? true : false
          handlerFunc(!buttonState)
          toggleTopPanelVisibility(panelVisibility)
        }}
        classNames={
          'functional-diagram-raised-button detail-diagram-detail-collapse-button'
        }
      />
    )
  }

  return (
    <>
      {!toComponentMaximized && !fromComponentMaximized && (
        <Flex
          className="functional-diagrams-info-panel-legend"
          gap="large"
          align={'center'}
        >
          {shouldHideInlineDetails && (
            <div>
              <h5>
                <FormattedMessage id="functionalDiagrams.detailDiagramView.circuit" />
              </h5>
              {circuit}
            </div>
          )}
          {shouldHideInlineDetails || (
            <div>
              <h5>
                <FormattedMessage id="functionalDiagrams.detailDiagramView.inline" />
              </h5>
              {`${toComponentAlias} - ${fromComponentAlias}`}
            </div>
          )}
          {shouldHideInlineDetails && (
            <div>
              <h5>
                <FormattedMessage id="functionalDiagrams.detailDiagramView.harness" />
              </h5>
              {isLoading ? (
                <Spin
                  indicator={<LoadingOutlined spin />}
                  size="small"
                  style={{ color: 'var(--color-text)' }}
                />
              ) : (
                harnessDescription
              )}
            </div>
          )}
          <div>
            <h5>
              <FormattedMessage id="functionalDiagrams.detailDiagramView.description" />
            </h5>
            {circuit_description}
          </div>
          {shouldHideInlineDetails && (
            <Flex vertical={true}>
              <h5>
                <FormattedMessage id="functionalDiagrams.detailDiagramView.color" />
              </h5>
              <Flex vertical={false} gap="small">
                <div>{colors.letter_code}</div>
                <div>
                  <CircuitColorIcon
                    firstColor={firstColor}
                    secondColor={secondColor}
                  />
                </div>
              </Flex>
            </Flex>
          )}
          <CircuitDTCsProvider>
            <RelatedDTCsButton
              loading={isMakeModelByVinLoading}
              circuit={circuit}
              makeModelId={makeModel?.id}
            />
            <RelatedDTCsModal />
          </CircuitDTCsProvider>
          {((fromComponentCategory == 'splice' && fromLocationImageName) ||
            (toComponentCategory == 'splice' && toLocationImageName)) && (
            <div data-cy="circuit-splice-toggle-button">
              <Button
                type={showSpliceLocationImage ? 'default' : 'primary'}
                onClick={spliceLocationImageDisplayHandler}
                size={isWindowBelowMinHeight() ? 'small' : 'middle'}
              >
                <LayoutOutlined />
              </Button>
            </div>
          )}
        </Flex>
      )}
      <Flex
        vertical={false}
        className={classNames('detail-diagrams-circuit-info-component-images', {
          'full-screen': fromComponentMaximized || toComponentMaximized,
        })}
      >
        {!toComponentMaximized && (
          <div
            className={classNames(
              'detail-diagrams-circuit-info-from-component',
              {
                'full-screen': fromComponentMaximized,
              },
            )}
          >
            {fromComponentCategory === 'splice' ? (
              fromLocationImageName && showSpliceLocationImage ? (
                <div
                  data-cy="circuit-splice-location-image"
                  style={{ height: '100%', position: 'relative' }}
                >
                  <LocationImage
                    fileName={fromLocationImageName}
                    componentId={fromComponent}
                    isFullScreen={fromComponentMaximized}
                  />
                  {renderExpandButton(
                    fromComponentMaximized,
                    toggleFromComponentView,
                  )}
                </div>
              ) : (
                <div data-testid="component-neighbour-table">
                  <ComponentNeighbors
                    componentId={fromComponent}
                    vin={diagram.vin}
                    pageSize={isWindowBelowMinHeight() ? 4 : 6}
                  />
                </div>
              )
            ) : (
              <div style={{ height: '100%', position: 'relative' }}>
                <ComponentOrLocationImage
                  componentImageName={fromComponentImageName}
                  pins={[fromPin]}
                  componentId={fromComponent}
                  locationImageName={fromLocationImageName}
                  description={fromComponentDescription}
                  alias={fromComponentAlias}
                  isFullScreen={fromComponentMaximized}
                  partNumber={fromComponentPartNumber}
                >
                  {fromComponentCategory !== 'inline' && (
                    <RelatedSubsystemsButton
                      showButtonText={false}
                      vin={diagram.vin}
                      componentId={fromComponent}
                      contextComponentId={
                        diagram.detailDiagram.relatedSubsystemComponentID
                      }
                      componentIdDispatch={
                        dispatch.handleRelatedSubsystemsButton
                      }
                    />
                  )}
                </ComponentOrLocationImage>
                {renderExpandButton(
                  fromComponentMaximized,
                  toggleFromComponentView,
                )}
              </div>
            )}
          </div>
        )}
        {!fromComponentMaximized && (
          <div
            className={classNames('detail-diagrams-circuit-info-to-component', {
              'full-screen': toComponentMaximized,
            })}
          >
            {toComponentCategory === 'splice' ? (
              toLocationImageName && showSpliceLocationImage ? (
                <div
                  data-cy="circuit-splice-location-image"
                  style={{ height: '100%', position: 'relative' }}
                >
                  <LocationImage
                    fileName={toLocationImageName}
                    componentId={toComponent}
                    isFullScreen={toComponentMaximized}
                  />
                  {renderExpandButton(
                    toComponentMaximized,
                    toggleToComponentView,
                  )}
                </div>
              ) : (
                <div data-testid="component-neighbour-table">
                  <ComponentNeighbors
                    componentId={toComponent}
                    vin={diagram.vin}
                    pageSize={isWindowBelowMinHeight() ? 4 : 6}
                  />
                </div>
              )
            ) : (
              <div style={{ height: '100%', position: 'relative' }}>
                <ComponentOrLocationImage
                  componentImageName={toComponentImageName}
                  pins={[toPin]}
                  componentId={toComponent}
                  locationImageName={toLocationImageName}
                  description={toComponentDescription}
                  alias={toComponentAlias}
                  isFullScreen={toComponentMaximized}
                  partNumber={toComponentPartNumber}
                >
                  {toComponentCategory !== 'inline' && (
                    <RelatedSubsystemsButton
                      showButtonText={false}
                      vin={diagram.vin}
                      componentId={toComponent}
                      contextComponentId={
                        diagram.detailDiagram.relatedSubsystemComponentID
                      }
                      componentIdDispatch={
                        dispatch.handleRelatedSubsystemsButton
                      }
                    />
                  )}
                </ComponentOrLocationImage>
                {renderExpandButton(
                  toComponentMaximized,
                  toggleToComponentView,
                )}
              </div>
            )}
          </div>
        )}
      </Flex>
    </>
  )
}

const DetailInfoComponentPanel = ({ toggleTopPanelVisibility }) => {
  const diagram = useDiagram()
  const dispatch = useDiagramDispatch()
  const props = useSelector((state) => {
    return {
      theme: state.app.theme,
    }
  }, shallowEqual)
  const {
    alias,
    description,
    componentId,
    locationImageName: detailDiagramLocationImageName,
    category: detailDiagramCategory,
    imageName,
    childPins,
    partNumber: detailDiagramPartNumber,
  } = diagram.detailDiagram.data

  const {
    componentId: componentNeighborId,
    imageName: componentNeighborImageName,
    locationImageName: componentNeighborLocationImageName,
    pin: componentNeighborPin,
    componentDescription,
    componentAlias,
    category,
    partNumber: componentNeighborPartNumber,
  } = diagram.detailDiagram.componentNeighbor.data

  const [componentMaximized, toggleComponentView] = useState(false)

  const displayDeviceImage =
    detailDiagramCategory !== 'splice' && detailDiagramCategory !== 'inline'

  const { isLoading: isMakeModelByVinLoading, makeModel } = useMakeModelByVin({
    vin: diagram.vin,
  })

  const shouldShowSpliceImage =
    detailDiagramCategory == 'splice' && detailDiagramLocationImageName

  const renderPanel = () => {
    if (!shouldShowSpliceImage && !componentNeighborId) {
      return <NoSelection />
    } else if (shouldShowSpliceImage && !componentNeighborId) {
      return (
        <div
          data-cy="splice-location-image"
          style={{ height: '100%', position: 'relative' }}
        >
          <LocationImage
            fileName={detailDiagramLocationImageName}
            componentId={componentId}
            isFullScreen={componentMaximized}
          />
          <FullScreenButton
            isDisabled={false}
            onClickHandler={() => {
              const panelVisibility = componentMaximized ? true : false
              toggleComponentView(!componentMaximized)
              toggleTopPanelVisibility(panelVisibility)
            }}
            classNames={
              'functional-diagram-raised-button detail-diagram-detail-collapse-button'
            }
          />
        </div>
      )
    } else if (componentNeighborId) {
      return (
        <div style={{ height: '100%', position: 'relative' }}>
          <ComponentOrLocationImage
            componentId={componentNeighborId}
            componentImageName={componentNeighborImageName}
            locationImageName={componentNeighborLocationImageName}
            pins={[componentNeighborPin]}
            description={componentDescription}
            alias={componentAlias}
            isFullScreen={componentMaximized}
            partNumber={componentNeighborPartNumber}
          >
            {category !== 'inline' && (
              <RelatedSubsystemsButton
                showButtonText={false}
                vin={diagram.vin}
                componentId={componentNeighborId}
                contextComponentId={
                  diagram.detailDiagram.relatedSubsystemComponentID
                }
                componentIdDispatch={dispatch.handleRelatedSubsystemsButton}
              />
            )}
          </ComponentOrLocationImage>
          <FullScreenButton
            isDisabled={false}
            onClickHandler={() => {
              const panelVisibility = componentMaximized ? true : false
              toggleComponentView(!componentMaximized)
              toggleTopPanelVisibility(panelVisibility)
            }}
            classNames={
              'functional-diagram-raised-button detail-diagram-detail-collapse-button'
            }
          />
        </div>
      )
    }
  }

  return (
    <Flex vertical={true} className="functional-diagrams-detail-info">
      {!displayDeviceImage && (
        <>
          {componentMaximized || (
            <Flex
              className="functional-diagrams-info-panel-legend"
              gap="large"
              align={'center'}
            >
              <div>
                <h5>
                  <FormattedMessage id="functionalDiagrams.detailDiagramView.detailInfoComponentPanel.alias" />
                </h5>
                {alias}
              </div>
              <div>
                <h5>
                  <FormattedMessage id="functionalDiagrams.detailDiagramView.detailInfoComponentPanel.description" />
                </h5>
                {description}
              </div>
            </Flex>
          )}
          <Flex
            className={classNames('detail-diagrams-component-info', {
              'full-screen': componentMaximized,
            })}
          >
            {componentMaximized || (
              <div className="detail-diagrams-component-info-table">
                <div className="component-neighbors-table-container">
                  <ComponentNeighbors
                    componentId={componentId}
                    vin={diagram.vin}
                    showSelection={
                      diagram.detailDiagram.selection === 'component'
                    }
                    pageSize={isWindowBelowMinHeight() ? 4 : 7}
                  />
                </div>
              </div>
            )}
            <div
              className={classNames('detail-diagrams-component-info-image', {
                'full-screen': componentMaximized,
              })}
            >
              {renderPanel()}
            </div>
          </Flex>
        </>
      )}
      {displayDeviceImage && (
        <div
          data-cy="device-image-container"
          style={{ height: '100%', position: 'relative' }}
        >
          <ComponentImage
            pins={childPins}
            fileName={imageName}
            theme={props.theme}
            isComponentImagePDBox={true}
          />
          <PartNumberLabel
            partNumber={detailDiagramPartNumber}
            className="functional-diagrams-component-or-location-view-part-number"
          />
        </div>
      )}
    </Flex>
  )
}

const FullScreenButton = ({ isDisabled, classNames, onClickHandler }) => {
  return (
    <div className={classNames}>
      <Button
        disabled={isDisabled}
        icon={<LayoutOutlined />}
        onClick={onClickHandler}
      />
    </div>
  )
}

function DiagramDetailView() {
  const diagram = useDiagram()
  return (
    <BaseDiagramDetailView
      renderBreadCrumbs={() => (
        <Breadcrumbs
          subsystem={diagram.subsystem?.name}
          fromComponentAlias={
            diagram.blockDiagram.graph.data.fromComponentAlias
          }
          toComponentAlias={diagram.blockDiagram.graph.data.toComponentAlias}
        />
      )}
      renderDiagram={({ vin, theme, zoomControlClassName }) => (
        <DetailDiagram
          key={`${vin}-${diagram.blockDiagram.graph.data.fromComponent}-${diagram.blockDiagram.graph.data.toComponent}`}
          vin={vin}
          fromComponent={diagram.blockDiagram.graph.data.fromComponent}
          toComponent={diagram.blockDiagram.graph.data.toComponent}
          theme={theme}
          zoomControlClassName={zoomControlClassName}
        />
      )}
    />
  )
}

const renderComponentDetailDiagram =
  ({ vin, subsystemCode, component, pins }) =>
  ({ theme, zoomControlClassName }) => (
    <ComponentDetailDiagram
      key={`${vin}-${component}`}
      vin={vin}
      subsystemCode={subsystemCode}
      component={component}
      pins={pins}
      theme={theme}
      zoomControlClassName={zoomControlClassName}
    />
  )

// Full-page component detail view (reached via its own route). Shows the
// subsystem breadcrumbs that navigate back to the functional diagram page.
function ComponentDetailDiagramView({ subsystemCode, component, pins }) {
  const diagram = useDiagram()
  const { subsystem } = useSubsystemByCode({
    vin: diagram.vin,
    subsystemCode,
  })

  return (
    <BaseDiagramDetailView
      renderBreadCrumbs={() => (
        <ComponentDetailDiagramBreadcrumbs
          vin={diagram.vin}
          subsystemCode={subsystemCode}
          subsystemName={subsystem?.name}
        />
      )}
      renderDiagram={renderComponentDetailDiagram({
        vin: diagram.vin,
        subsystemCode,
        component,
        pins,
      })}
    />
  )
}

// Inline detail view embedded in the right panel for a selection of connector
// pins. Shows a back button + "Pins Detail Diagram" label and returns to the
// pinout via onBack.
function PinsDetailDiagramView({
  subsystemCode,
  component,
  alias,
  pins,
  onBack,
}) {
  const diagram = useDiagram()
  const dispatch = useDiagramDispatch()
  const intl = useIntl()

  // Clear the detail-diagram selection when leaving this view (via back or a
  // connector change) so a stale circuit/pin panel doesn't leak into the next
  // visit.
  useEffect(() => {
    return () => {
      dispatch.detailDiagram.reset()
    }
  }, [])

  const renderBreadCrumbs = () => {
    const title = intl.formatMessage({
      id: 'functionalDiagrams.detailDiagramView.pinsDetailDiagram',
    })
    const pinsSuffix =
      pins?.length > 0
        ? ` — ${intl.formatMessage(
            { id: 'functionalDiagrams.detailDiagramView.pinsLabel' },
            { count: pins.length },
          )} ${pins.join(', ')}`
        : ''
    const label = `${title}: ${alias || component}${pinsSuffix}`

    const items = [
      {
        href: '',
        onClick: (e) => {
          e.preventDefault()
          onBack()
        },
        title: <LeftOutlined data-testid="detail-diagram-back-button" />,
      },
      {
        title: <span title={label}>{label}</span>,
      },
    ]

    return (
      <Breadcrumb
        className="functional-diagrams-detail-diagram-breadcrumb"
        items={items}
      />
    )
  }

  return (
    <BaseDiagramDetailView
      renderBreadCrumbs={renderBreadCrumbs}
      renderDiagram={renderComponentDetailDiagram({
        vin: diagram.vin,
        subsystemCode,
        component,
        pins,
      })}
    />
  )
}

function BaseDiagramDetailView({ renderDiagram, renderBreadCrumbs }) {
  const diagram = useDiagram()
  const dispatch = useDiagramDispatch()
  const [showBottomPanel, toggleBottomPanelVisibility] = useState(false)
  const [showTopPanel, toggleTopPanelVisibility] = useState(true)
  const timeOnHarness = useRef(null)
  const props = useSelector((state) => {
    return {
      theme: state.app.theme,
    }
  }, shallowEqual)
  const [userPlant] = useUserPlant()

  const InfoPanelRouter = React.useCallback(() => {
    let ComponentToRender

    switch (diagram.detailDiagram.selection) {
      case 'pin':
        ComponentToRender = DetailInfoPinPanel
        break
      case 'circuit':
        ComponentToRender = DetailInfoCircuit
        break
      case 'component':
        ComponentToRender = DetailInfoComponentPanel
        break
      default:
        ComponentToRender = NoSelection
    }
    return (
      <ComponentToRender toggleTopPanelVisibility={toggleTopPanelVisibility} />
    )
  }, [diagram.detailDiagram.selection])

  useEffect(() => {
    window.addEventListener('beforeunload', dispatchHarnessEvent)
    return () => {
      window.removeEventListener('beforeunload', dispatchHarnessEvent)
    }
  }, [])

  useEffect(() => {
    if (diagram.detailDiagram.selection) toggleBottomPanelVisibility(true)
  }, [
    diagram.detailDiagram.selection,
    diagram.detailDiagram.data.fromComponent,
    diagram.detailDiagram.data.toComponent,
  ])

  useEffect(() => {
    toggleBottomPanelVisibility(false)
    timeOnHarness.current = Date.now()
    return () => {
      dispatchHarnessEvent()
    }
  }, [
    diagram.blockDiagram.graph.data.fromComponent,
    diagram.blockDiagram.graph.data.toComponent,
  ])

  const dispatchHarnessEvent = () => {
    if (timeOnHarness.current) {
      console.log('sending the following usage event:', {
        harnessId: `${diagram.blockDiagram.graph.data.fromComponentAlias} - ${diagram.blockDiagram.graph.data.toComponentAlias}`,
        timeOnHarness: Date.now() - timeOnHarness.current,
        plant: userPlant,
      })
      dispatchEvent('FUNCTIONAL_DIAGRAM_HARNESS_UPDATED', {
        harnessId: `${diagram.blockDiagram.graph.data.fromComponentAlias} - ${diagram.blockDiagram.graph.data.toComponentAlias}`,
        timeOnHarness: Date.now() - timeOnHarness.current,
        plant: userPlant,
      })
    }
  }

  return (
    <Flex vertical={true} style={{ height: '100%' }}>
      <div className="functional-diagrams-breadcrumb">
        {renderBreadCrumbs()}
      </div>
      <div className="functional-diagrams-detail-panel-content">
        <div
          className={classNames('detail-diagram-container', {
            'full-screen': !showBottomPanel,
            hide: !showTopPanel,
          })}
        >
          <TracerErrorBoundary
            resetKeys={[
              diagram.vin,
              diagram.blockDiagram.graph.data.fromComponent,
              diagram.blockDiagram.graph.data.toComponent,
            ]}
          >
            {renderDiagram({
              vin: diagram.vin,
              theme: props.theme,
              zoomControlClassName: showBottomPanel
                ? 'detail-diagram-zoom-control'
                : 'detail-diagram-full-zoom-control',
            })}
            <FullScreenButton
              isDisabled={diagram.detailDiagram.selection ? false : true}
              onClickHandler={() =>
                toggleBottomPanelVisibility(!showBottomPanel)
              }
              classNames={
                'functional-diagram-raised-button detail-diagram-detail-expand-button'
              }
            />
          </TracerErrorBoundary>
        </div>
        <div
          data-testid="functional-diagrams-detail-info-panel"
          className={classNames('ffunctional-diagrams-detail-info-panel', {
            hide: !showBottomPanel,
            'full-screen': !showTopPanel,
          })}
        >
          <InfoPanelRouter />
        </div>
      </div>
    </Flex>
  )
}
export { ComponentDetailDiagramView, PinsDetailDiagramView }
export default DiagramDetailView
